import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  Flex, Icon, Text, Button, Spinner, useColorModeValue, useToast,
} from "@chakra-ui/react";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { settings } from "../../../shared/settings";
import { useSubmission } from "./useSubmission";
import { DynamicFormRenderer } from "./DynamicFormRenderer";
import { ErrorState } from "../../../components/ErrorState";
import { getFormIcon, getFormActionLabel } from "./formPresentation";
import { validateForm } from "./formValidation";
import type { FieldValue, FormField, FormType, FormTypeWithFields } from "./types";

const base = () => settings.environment.VITE_PROCESS_HUB_BASE_URL ?? "";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formType: FormType | null;
}

export function SubmissionFormModal({ isOpen, onClose, formType }: Props) {
  const toast = useToast();
  const { request } = useApiRequest();
  const { createSubmission, saveDraft, submit, uploadFile } = useSubmission();

  const titleColor = useColorModeValue("#434347", "#e8edf2");
  const cardBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const iconChipBg = useColorModeValue("rgba(0, 158, 251, 0.1)", "rgba(0, 158, 251, 0.16)");

  const [fields, setFields] = useState<FormField[] | null>(null);
  const [values, setValues] = useState<Record<number, FieldValue>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState<Record<number, boolean>>({});

  const validation = useMemo(
    () => (fields ? validateForm(fields, values) : { isValid: true, errors: {} }),
    [fields, values]
  );

  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const creatingRef = useRef<Promise<number> | null>(null);

  const load = useCallback(async () => {
    if (!formType) return;
    try {
      setLoading(true);
      setError(false);
      const formData = (await request({
        url: `${base()}/api/v1/forms/${formType.id}`,
        method: "GET",
        skipToast: true,
      })) as FormTypeWithFields;
      setFields(formData.fields.filter((f) => f.is_active));
      setValues({});
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [formType, request]);

  useEffect(() => {
    if (isOpen) {
      load();
    } else {
      setSubmissionId(null);
      creatingRef.current = null;
      setFields(null);
      setValues({});
      setTouched({});
    }
  }, [isOpen, load]);

  const ensureSubmission = useCallback(async (): Promise<number> => {
    if (submissionId) return submissionId;
    if (creatingRef.current) return creatingRef.current;
    if (!formType) throw new Error("Tipo de formulário ausente");
    const pending = createSubmission(formType.id).then((s) => {
      setSubmissionId(s.id);
      creatingRef.current = null;
      return s.id;
    });
    creatingRef.current = pending;
    return pending;
  }, [submissionId, formType, createSubmission]);

  const handleChange = (fieldId: number, value: string | null, valueOptions?: string[] | null) => {
    setValues((prev) => ({
      ...prev,
      [fieldId]: {
        field_definition_id: fieldId,
        value: value ?? prev[fieldId]?.value ?? null,
        value_options: valueOptions ?? prev[fieldId]?.value_options ?? null,
      },
    }));
    setTouched((prev) => (prev[fieldId] ? prev : { ...prev, [fieldId]: true }));
  };

  const toFieldValues = (): FieldValue[] => Object.values(values);

  const handleSaveDraft = async () => {
    if (!formType || !fields) return;
    const draftValidation = validateForm(fields, values, { requireRequired: false });
    if (!draftValidation.isValid) {
      setTouched((prev) => {
        const next = { ...prev };
        Object.keys(draftValidation.errors).forEach((id) => (next[Number(id)] = true));
        return next;
      });
      toast({ title: "Verifique os campos destacados antes de salvar.", status: "warning", duration: 6000, isClosable: true });
      return;
    }
    setSaving(true);
    try {
      const id = await ensureSubmission();
      await saveDraft(id, toFieldValues());
    } catch {
      toast({ title: "Erro ao iniciar solicitação", status: "error", duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formType) return;
    if (!validation.isValid) return;
    setSaving(true);
    try {
      const id = await ensureSubmission();
      await submit(id, toFieldValues());
      onClose();
    } catch {
      toast({ title: "Erro ao enviar solicitação", status: "error", duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFile = async (fieldId: number, file: File) => {
    try {
      const id = await ensureSubmission();
      return await uploadFile(id, fieldId, file);
    } catch {
      toast({ title: "Erro ao iniciar solicitação", status: "error", duration: 6000, isClosable: true });
      throw new Error("Falha ao iniciar solicitação para upload");
    }
  };

  const FormIcon = formType ? getFormIcon(formType.name) : undefined;
  const ready = !loading && !error && fields !== null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="2xl" mx={4} maxH="85vh">
        <ModalHeader borderBottomWidth="1px" borderColor={cardBorder} pb={4}>
          <Flex align="center" gap={3}>
            {FormIcon && (
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="10px" bg={iconChipBg} flexShrink={0}>
                <Icon as={FormIcon} boxSize={5} color="#009efb" />
              </Flex>
            )}
            <Text fontSize="lg" fontWeight={700} color={titleColor}>{formType?.name}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={5} />

        <ModalBody py={5}>
          {loading ? (
            <Flex justify="center" align="center" py={16}>
              <Spinner size="md" color="#009efb" />
            </Flex>
          ) : error || fields === null ? (
            <ErrorState onRetry={load} />
          ) : (
            <DynamicFormRenderer
              fields={fields}
              values={values}
              onChange={handleChange}
              onUploadFile={handleUploadFile}
              errors={validation.errors}
              touched={touched}
            />
          )}
        </ModalBody>

        {ready && (
          <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={3} pt={4}>
            <Button variant="ghost" borderRadius="50px" size="sm" onClick={handleSaveDraft} isLoading={saving}>
              Salvar rascunho
            </Button>
            <Button
              flex={1}
              bg="#009efb"
              color="white"
              _hover={{ bg: "#007bd4" }}
              borderRadius="50px"
              isLoading={saving}
              isDisabled={!validation.isValid}
              onClick={handleSubmit}
            >
              {formType ? getFormActionLabel(formType.name) : "Enviar"}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}